import { Content } from '@elizaos/core';

export interface DeFiPosition {
  id: string;
  label: string;
  symbol: string;
  network: string;
  address: string;
  balance: string;
  balanceUSD: number;
  price: number;
  tokenAddress: string;
  decimals: number;
  type: PositionType;
  protocol: string;
  protocolDisplayName: string;
  protocolIconUrl?: string;
  apr?: number;
  apy?: number;
  rewards?: RewardPosition[];
  metadata?: PositionMetadata;
}

export interface RewardPosition {
  symbol: string;
  name: string;
  balance: string;
  balanceUSD: number;
  price: number;
  tokenAddress: string;
  decimals: number;
  network: string;
  type: 'reward' | 'claimable' | 'pending';
}

export interface PositionMetadata {
  maturityDate?: string;
  liquidationPrice?: number;
  healthFactor?: number;
  collateralRatio?: number;
  borrowRate?: number;
  supplyRate?: number;
  poolShare?: number;
  totalSupply?: string;
  totalBorrow?: string;
  utilization?: number;
  liquidityToken?: string;
  stakingRewards?: string[];
  yieldTokens?: string[];
  fee?: number;
  volume24h?: number;
  tvl?: number;
  pair?: {
    token0: string;
    token1: string;
    reserve0: string;
    reserve1: string;
  };
}

export enum PositionType {
  // Staking positions
  STAKING = 'staking',
  VALIDATOR = 'validator',
  DELEGATION = 'delegation',

  // Lending positions
  LENDING_SUPPLY = 'lending-supply',
  LENDING_BORROW = 'lending-borrow',
  LENDING_COLLATERAL = 'lending-collateral',

  // Liquidity positions
  LIQUIDITY_POOL = 'liquidity-pool',
  LIQUIDITY_TOKEN = 'liquidity-token',
  FARM = 'farm',

  // Yield farming
  YIELD_FARM = 'yield-farm',
  YIELD_VAULT = 'yield-vault',
  YIELD_STRATEGY = 'yield-strategy',

  // Governance
  GOVERNANCE = 'governance',
  VOTING = 'voting',

  // Derivatives
  PERPETUAL = 'perpetual',
  OPTION = 'option',
  FUTURE = 'future',

  // Insurance
  INSURANCE = 'insurance',
  COVERAGE = 'coverage',

  // Other
  WALLET = 'wallet',
  CLAIMABLE = 'claimable',
  VESTING = 'vesting',
}

export interface AppPosition {
  appId: string;
  appName: string;
  appDisplayName: string;
  appIconUrl?: string;
  network: string;
  address: string;
  positions: DeFiPosition[];
  totalValueUSD: number;
  metadata?: AppMetadata;
}

export interface AppMetadata {
  description?: string;
  website?: string;
  twitter?: string;
  discord?: string;
  telegram?: string;
  documentation?: string;
  github?: string;
  category?: string;
  tags?: string[];
  verified?: boolean;
  tvl?: number;
  volume24h?: number;
  fees24h?: number;
  users?: number;
  transactions?: number;
  chains?: string[];
  protocols?: string[];
  lastUpdated?: string;
}

export interface AppPositionsData {
  address: string;
  totalValueUSD: number;
  networks: string[];
  apps: AppPosition[];
  positionCount: number;
  appCount: number;
  networkCount: number;
  summary: {
    staking: number;
    lending: number;
    liquidity: number;
    farming: number;
    other: number;
  };
}

export interface GetAppPositionsContent extends Content {
  address: string;
  networks?: string[] | string;
  appIds?: string[] | string;
  positionTypes?: PositionType[] | PositionType;
  minValueUSD?: number;
  includeRewards?: boolean;
  includeMetadata?: boolean;
}

export interface StakingPosition extends DeFiPosition {
  type: PositionType.STAKING | PositionType.VALIDATOR | PositionType.DELEGATION;
  validatorAddress?: string;
  delegationRewards?: RewardPosition[];
  unbondingPeriod?: number;
  slashingRisk?: number;
  commission?: number;
  stakingPeriod?: {
    start: string;
    end?: string;
    duration?: number;
  };
}

export interface LendingPosition extends DeFiPosition {
  type: PositionType.LENDING_SUPPLY | PositionType.LENDING_BORROW | PositionType.LENDING_COLLATERAL;
  supplyBalance?: string;
  borrowBalance?: string;
  collateralBalance?: string;
  supplyRate?: number;
  borrowRate?: number;
  healthFactor?: number;
  liquidationThreshold?: number;
  liquidationPrice?: number;
  borrowCapacity?: string;
  borrowCapacityUSD?: number;
}

export interface LiquidityPosition extends DeFiPosition {
  type: PositionType.LIQUIDITY_POOL | PositionType.LIQUIDITY_TOKEN | PositionType.FARM;
  token0?: string;
  token1?: string;
  reserve0?: string;
  reserve1?: string;
  poolShare?: number;
  fee?: number;
  volume24h?: number;
  fees24h?: number;
  impermanentLoss?: number;
  lpTokenBalance?: string;
  lpTokenPrice?: number;
}

export interface YieldFarmPosition extends DeFiPosition {
  type: PositionType.YIELD_FARM | PositionType.YIELD_VAULT | PositionType.YIELD_STRATEGY;
  stakedBalance?: string;
  stakedBalanceUSD?: number;
  earnedBalance?: string;
  earnedBalanceUSD?: number;
  strategyName?: string;
  strategyDescription?: string;
  autoCompound?: boolean;
  harvestFee?: number;
  performanceFee?: number;
  withdrawalFee?: number;
  lockupPeriod?: number;
}

export interface DeFiAppInfo {
  appId: string;
  name: string;
  displayName: string;
  description?: string;
  iconUrl?: string;
  website?: string;
  category: string;
  tags: string[];
  networks: string[];
  protocols: string[];
  verified: boolean;
  tvl?: number;
  volume24h?: number;
  fees24h?: number;
  users?: number;
  transactions?: number;
  lastUpdated: string;
  social?: {
    twitter?: string;
    discord?: string;
    telegram?: string;
    github?: string;
  };
}

export interface DeFiProtocolInfo {
  protocolId: string;
  name: string;
  displayName: string;
  description?: string;
  iconUrl?: string;
  website?: string;
  category: string;
  networks: string[];
  tvl?: number;
  volume24h?: number;
  fees24h?: number;
  users?: number;
  verified: boolean;
  risks?: string[];
  audits?: string[];
  lastUpdated: string;
}

export interface AppDiscoveryFilter {
  networks?: string[];
  categories?: string[];
  tags?: string[];
  verified?: boolean;
  minTvl?: number;
  minVolume24h?: number;
  hasPositions?: boolean;
  search?: string;
}

export interface AppDiscoveryResult {
  apps: DeFiAppInfo[];
  total: number;
  page: number;
  limit: number;
  filters: AppDiscoveryFilter;
}
